import { Link } from 'react-router-dom';
import { CheckCircle2, Lock, LogIn } from 'lucide-react';
import { IMAGES } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';
import TradingViewTicker from './TradingViewTicker';
import TradingViewOverview from './TradingViewOverview';

export default function TradingSection() {
  const { t } = useLang();
  const { isAuth, user } = useAuth();

  return (
    <section id="trading" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-brand-navy">
        <img src={IMAGES.port} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy to-[#0f1a5c]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <span className="inline-block text-brand-yellow text-xs font-semibold tracking-[0.35em] uppercase mb-4 border border-brand-yellow/40 px-4 py-2 rounded-full">
            {t('trading.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {t('trading.title1')} <span className="text-brand-yellow">{t('trading.title2')}</span>
          </h2>
        </div>

        {/* Live ticker tape */}
        <div className="mb-4 rounded-lg overflow-hidden border border-white/10">
          <TradingViewTicker />
        </div>

        {/* Market overview chart */}
        <div className="rounded-xl overflow-hidden border border-white/10 bg-brand-navy mb-16">
          <TradingViewOverview />
        </div>

        {/* Benefits + Private access */}
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="text-white">
            <h3 className="text-3xl font-bold mb-6">{t('trading.whyTitle')}</h3>
            <div className="space-y-5">
              {t('trading.benefits').map((b, i) => (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">{b.t}</div>
                    <div className="text-white/70 text-sm mt-1">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Private producer access card */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-yellow/15 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow mb-4">
                <Lock className="w-6 h-6 text-brand-navy" />
              </div>
              <h3 className="text-2xl font-bold text-brand-navy mb-2">{t('ext.pa_titleShort')}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('ext.pa_descShort_pre')}<strong>{t('ext.pa_descShort_docs')}</strong>{t('ext.pa_descShort_mid')}<strong>{t('ext.pa_descShort_bank')}</strong>{t('ext.pa_descShort_mid')}<strong>{t('ext.pa_descShort_pass')}</strong>{t('ext.pa_descShort_and')}<strong>{t('ext.pa_descShort_lic')}</strong>{t('ext.pa_descShort_of')}
              </p>
              <ul className="space-y-2 mb-6 text-sm">
                {[
                  t('ext.pa_bullet1'),
                  t('ext.pa_bullet2'),
                  t('ext.pa_bullet3'),
                  t('ext.pa_bullet4'),
                ].map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-brand-navy">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow shrink-0" /> {it}
                  </li>
                ))}
              </ul>

              {isAuth ? (
                <Link to={user?.role === 'admin' ? '/admin' : '/extranet'} className="btn-yellow w-full justify-center">
                  <LogIn className="w-4 h-4" /> {t('ext.pa_ctaEnter')}
                </Link>
              ) : (
                <Link to="/extranet/login" className="btn-yellow w-full justify-center">
                  <LogIn className="w-4 h-4" /> {t('ext.pa_ctaSignIn')}
                </Link>
              )}
              <p className="text-xs text-gray-500 mt-4 text-center">
                🔒 {t('ext.pa_contactLock')} <a href="mailto:denis@yuville.com" className="text-brand-navy font-semibold hover:underline">denis@yuville.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
